'use client';

import {evite} from 'evite';
import {observable} from 'mobx';
import urls from 'urls';
import XDate from 'xdate';
import {IndexSearchModel} from './IndexSearchModel';

let intervalId = 0;
const FIVE_MINUTES = 1000 * 60 * 5;
const memoise = new Map();
const MAX_SUGGESTION_RESULTS = 6;
const TRENDING_SEARCH_DAYS = 1;
const TRENDING_SEARCH_URL_NAME = 'ajax_gallery_searches';
const SUGGESTIONS_URL_NAME = 'ajax_gallery_suggestions';

// TODO - refactor

export class SearchModel {
  @observable accessor ready = false;

  invitation = {};

  card = {};

  cache = new Map();

  constructor(attributes = {}) {
    Object.assign(this, attributes);

    this.invitationModel = new IndexSearchModel(this.invitation || {});
    this.cardModel = new IndexSearchModel(this.card || {});

    if (!intervalId) {
      intervalId = setInterval(() => {
        this.cache.clear();
      }, FIVE_MINUTES);
    }
  }

  parse(json = {}) {
    const parsedJson = {...json};
    parsedJson.invitation = new IndexSearchModel(json.invitation || {});
    parsedJson.card = new IndexSearchModel(json.card || {});
    return parsedJson;
  }

  static normalizeSearch(search) {
    if (memoise.has(search)) {
      return memoise.get(search);
    }

    const allWhitespaces = /\s+/g;
    const normalizedString = (search || '').trim().toLowerCase().replace(allWhitespaces, ' ');

    memoise.set(search, normalizedString);
    return normalizedString;
  }

  executeSuggestionsRequest(url) {
    return evite
      .fetch(`${url}`, {
        credentials: 'include',
        headers: {
          'X-CSRFToken': evite.cookie('csrftoken'),
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      })
      .then((response) => (response.ok ? response.json() : Promise.reject(response.statusText)));
  }

  transformSuggestionResults(results, filters) {
    if (filters.length === 1) {
      const dict = {};
      dict[filters[0]] = results;
      return dict;
    }
    return results;
  }

  fetchSuggestions(filters, search) {
    const url = `${urls.get(this.urlName, {search})}?filter=${filters.map((f) => f).join('&filter=')}`;
    return this.executeSuggestionsRequest(url).then((json) =>
      this.transformSuggestionResults(json, filters)
    );
  }

  transformTrendingSearchResults(results, filters) {
    if (filters.length === 1) {
      const dict = {};
      dict[filters[0]] = {suggestions: results};
      return dict;
    }
    return results;
  }

  fetchTrendingSearches(filters) {
    const filter = filters[0];
    const today = new XDate();
    const dateFormat = 'yyyy-MM-dd';
    const url = urls.get(this.urlName, {
      endDate: today.toString(dateFormat),
      startDate: today.addDays(-TRENDING_SEARCH_DAYS).toString(dateFormat),
      limit: MAX_SUGGESTION_RESULTS,
      filter,
    });

    return this.executeSuggestionsRequest(url).then((json) =>
      this.transformTrendingSearchResults(json, [filter])
    );
  }

  fetchSearchTerm(search, filters) {
    // biome-ignore lint/style/noParameterAssign: existing functionality
    search = SearchModel.normalizeSearch(search);
    this.search = search;
    this.urlName = search ? SUGGESTIONS_URL_NAME : TRENDING_SEARCH_URL_NAME;
    const searchFilters = !Array.isArray(filters) ? [filters] : filters;
    const cacheKey = `search=${search}&filters=${filters.map((f) => f).join(',')}`;

    if (this.cache.has(cacheKey)) {
      this.currentFetch = this.cache.get(cacheKey);
      return this.currentFetch;
    }

    this.currentFetch = (
      this.search
        ? this.fetchSuggestions(searchFilters, search)
        : this.fetchTrendingSearches(searchFilters)
    )
      .then((json) => ({search, ...(json || {})}))
      .catch((error) => {
        evite.error(`Error while retrieving search suggestions. errorThrown= ${error}`);
      });
    this.cache.set(cacheKey, this.currentFetch);
    return this.currentFetch;
  }

  async sync(searchOrJson) {
    this.ready = false;

    let json;
    if (typeof searchOrJson === 'string') {
      json = await this.fetchSearchTerm(searchOrJson);
    } else {
      json = searchOrJson;
    }

    Object.assign(this, this.parse(json));
    this.invitationModel.sync(this.invitation);
    this.cardModel.sync(this.card);

    this.ready = true;
  }
}
